// Section registry — explicit order + per-section metadata.
// Add a section here, then articles can reference its id via frontmatter.
//
// `icon` is a Font Awesome v6 class suffix (e.g., `fa-rocket` → rendered as
// `<i class="fa-solid fa-rocket">`). FA is loaded from CDN in index.html.

// Top-level "areas" of the help center. Each area has its own sidebar and its
// own URL space; the top nav toggles between them. `docs` lives at the root
// (`/<slug>`); `api` lives under `/api/<slug>`.
export type AreaId = 'docs' | 'api';

export type Area = {
  id: AreaId;
  label: string;
  /** Where the top-nav tab for this area points. */
  home: string;
};

export const AREAS: Area[] = [
  { id: 'docs', label: 'Documentation', home: '/' },
  { id: 'api', label: 'API', home: '/api/api-overview' },
];

export type Section = {
  id: string;
  title: string;
  icon: string;
  summary: string;
  /** Which area this section belongs to. Defaults to `docs` when omitted. */
  area: AreaId;
};

export const SECTIONS: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'fa-rocket',
    summary: 'Quickstart, core concepts, and your first project.',
    area: 'docs',
  },
  {
    id: 'account',
    title: 'Account & Security',
    icon: 'fa-user-shield',
    summary: 'Manage your profile, sign-in methods, passkeys, and notifications.',
    area: 'docs',
  },
  {
    id: 'workspaces',
    title: 'Workspaces',
    icon: 'fa-building',
    summary: 'Create workspaces, invite members, organize teams, and tune settings.',
    area: 'docs',
  },
  {
    id: 'projects',
    title: 'Projects',
    icon: 'fa-folder-open',
    summary: 'Create schema projects, manage permissions, and configure SQL dialect.',
    area: 'docs',
  },
  {
    id: 'editor',
    title: 'Schema Editor',
    icon: 'fa-diagram-project',
    summary: 'Design schemas on the ERD canvas, edit tables, and review draft changes.',
    area: 'docs',
  },
  {
    id: 'ai',
    title: 'AI Assistance',
    icon: 'fa-wand-magic-sparkles',
    summary: 'Design schemas with Claude, the Librarian, and AI suggestions.',
    area: 'docs',
  },
  {
    id: 'versions',
    title: 'Versions & SQL',
    icon: 'fa-code-commit',
    summary: 'Version your schema, generate SQL and migrations, and save queries.',
    area: 'docs',
  },
  {
    id: 'integrations',
    title: 'Integrations & Data',
    icon: 'fa-plug',
    summary: 'Connect via MCP, API tokens, and import or export your schema.',
    area: 'docs',
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: 'fa-circle-question',
    summary: 'Answers to common questions about Schedata.',
    area: 'docs',
  },
  // ── API area ──────────────────────────────────────────────────
  {
    id: 'api-guides',
    title: 'Guides',
    icon: 'fa-book',
    summary: 'Authenticate, make your first request, and handle errors.',
    area: 'api',
  },
  {
    id: 'api-reference',
    title: 'Reference',
    icon: 'fa-list',
    summary: 'The REST endpoint catalog, MCP tools, and token scopes.',
    area: 'api',
  },
];

export function findSection(id: string): Section | undefined {
  return SECTIONS.find(s => s.id === id);
}

/** The area a section belongs to (defaults to `docs` for unknown ids). */
export function areaForSection(id: string): AreaId {
  return SECTIONS.find(s => s.id === id)?.area ?? 'docs';
}

/** Which area a URL path belongs to. `/api` and `/api/*` are the API area. */
export function areaForPath(pathname: string): AreaId {
  return pathname === '/api' || pathname.startsWith('/api/') ? 'api' : 'docs';
}
